import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { fmtDate } from '@/pages/incentivation/lib'
import type { IncentivationFilterValues } from './IncentivationFilters'
import { numericScore } from './ScoreCell'
import { useInstances } from './useInstances'

interface Props {
  moduleId: number
  employeeId: string
  applied: IncentivationFilterValues
}

/**
 * "Score-Entwicklung" — line chart of the selected employee's numeric score over time
 * (x = instance start date, y = score / entered reward). Only mounted for modules that
 * carry a plottable value (Score / ManualInput); shares the cached instances query.
 */
export function ScoreDevelopmentChart({ moduleId, employeeId, applied }: Props) {
  const { t } = useTranslation('action-modules-detail')
  const { instances } = useInstances(moduleId, employeeId, applied)

  const data = useMemo(() => {
    return instances
      .map((inst) => ({ start: inst.startDate, value: numericScore(inst.incentivationData) }))
      .filter((point): point is { start: string; value: number } => point.value != null && !!point.start)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      .map((point) => ({ date: fmtDate(point.start), value: point.value }))
  }, [instances])

  return (
    <div className="flex h-full min-h-64 flex-col p-4">
      <p className="mb-3 text-sm font-semibold">{t('incentivation.graph.title')}</p>
      <div className="min-h-0 flex-1">
        {data.length === 0 ? (
          <div className="flex h-full min-h-40 items-center justify-center text-sm text-muted-foreground">
            {t('incentivation.noInstances')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(value) => [value, t('incentivation.graph.score')]}
                contentStyle={{ background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
              />
              <Line type="monotone" dataKey="value" stroke="var(--primary)" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
